import { useState, useCallback } from 'react';
import { getPublicProductVersion, VersionData } from '../services/publicProductService';

interface UseProductVersionProps {
    productId?: string;
    initialVersion?: number;
    totalVersions?: number;
}

// Lets public product viewers browse historical versions (versionData null = showing latest)
export const useProductVersion = ({ productId, initialVersion = 0, totalVersions = 1 }: UseProductVersionProps) => {
    const [currentVersion, setCurrentVersion] = useState<number>(initialVersion);
    const [versionData, setVersionData] = useState<VersionData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handlePreviousVersion = useCallback(async () => {
        if (!productId || currentVersion <= 1 || isLoading) return;

        const newVersion = currentVersion - 1;
        setIsLoading(true);

        try {
            const data = await getPublicProductVersion(productId, newVersion);
            setVersionData(data);
            setCurrentVersion(newVersion);
        } catch (err) {
            console.error('Failed to load product version:', err);
        } finally {
            setIsLoading(false);
        }
    }, [productId, currentVersion, isLoading]);

    const handleNextVersion = useCallback(async () => {
        if (!productId || currentVersion >= totalVersions || isLoading) return;

        const newVersion = currentVersion + 1;

        // If going back to latest, clear version data
        if (newVersion === totalVersions) {
            setVersionData(null);
            setCurrentVersion(newVersion);
            return;
        }

        setIsLoading(true);

        try {
            const data = await getPublicProductVersion(productId, newVersion);
            setVersionData(data);
            setCurrentVersion(newVersion);
        } catch (err) {
            console.error('Failed to load product version:', err);
        } finally {
            setIsLoading(false);
        }
    }, [productId, currentVersion, totalVersions, isLoading]);

    const jumpToVersion = useCallback(async (version: number) => {
        if (!productId || version < 1 || version > totalVersions || isLoading) return;

        // If jumping to latest, clear version data
        if (version === totalVersions) {
            setVersionData(null);
            setCurrentVersion(version);
            return;
        }

        // If jumping to current, do nothing
        if (version === currentVersion) return;

        setIsLoading(true);

        try {
            const data = await getPublicProductVersion(productId, version);
            setVersionData(data);
            setCurrentVersion(version);
        } catch (err) {
            console.error('Failed to load product version:', err);
        } finally {
            setIsLoading(false);
        }
    }, [productId, currentVersion, totalVersions, isLoading]);

    const resetToLatest = useCallback(() => {
        setVersionData(null);
        setCurrentVersion(totalVersions);
    }, [totalVersions]);

    return {
        currentVersion,
        setCurrentVersion,
        versionData,
        setVersionData,
        isLoading,
        handlePreviousVersion,
        handleNextVersion,
        jumpToVersion,
        resetToLatest,
        totalVersions
    };
};
